// client\src\hooks\useAuth.js
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    signIn,
    forgotPassword,
    resetPassword
} from '../services/authService';
import { setToken, removeToken } from '../utils/jwtUtils';

/**
 * Hook to manage user authentication
 */
export const useAuth = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const navigate = useNavigate();

    // Sign In
    const handleSignIn = async (email, password, returnURL = '/') => {
        setLoading(true);
        setError(null);
        try {
            const authenticatedUser = await signIn(email, password);

            // Check if token is received
            if (!authenticatedUser.token) {
                throw new Error("No token received. Please check your API response.");
            }

            setToken(authenticatedUser.token);
            setIsAuthenticated(true);
            navigate(returnURL); // Redirect to the specified return URL
        } catch (err) {
            console.error("Sign In Error:", err);
            setError(
                err.response?.data?.message ||
                err.message ||
                "Failed to sign in. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    // Forgot Password - Step 1: Request OTP
    const handleForgotPassword = async (email) => {
        setLoading(true);
        setError(null);
        setSuccessMessage('');
        try {
            const message = await forgotPassword(email);
            setSuccessMessage(message); // Display success message that OTP is sent
        } catch (err) {
            console.error("Forgot Password Error:", err);
            setError(
                err.response?.data?.message ||
                err.message ||
                "Failed to send OTP. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    // Reset Password - Step 2: Verify OTP and Reset Password
    const handleResetPassword = async (email, otp, newPassword) => {
        setLoading(true);
        setError(null);
        setSuccessMessage('');
        try {
            const message = await resetPassword(email, otp, newPassword);
            setSuccessMessage(message);
            navigate('/signin'); // Redirect to sign in after successful reset
        } catch (err) {
            console.error("Reset Password Error:", err);
            setError(
                err.response?.data?.message ||
                err.message ||
                "Failed to reset password. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    // Sign Out
    const handleSignOut = () => {
        removeToken();
        setIsAuthenticated(false);
        navigate('/login');
    };

    return {
        isAuthenticated,
        loading,
        error,
        successMessage, // Added for success messages
        handleSignIn,
        handleSignOut,
        handleForgotPassword,
        handleResetPassword
    };
};
